import * as React from "react";
import type { SAProperty } from "@/data/sellerAnalysisTypes";
import { formatPriceFull, locationLine } from "@/lib/format";
import { Reveal, Overline } from "@/components/presentation/primitives";

interface Props {
  property: SAProperty;
}

interface Item {
  label: string;
  value: React.ReactNode;
}

export const SA_PropertySummary: React.FC<Props> = ({ property }) => {
  const image = property.images?.[1] || property.images?.[0];
  const location = locationLine(property);

  const items: Item[] = [];
  if (property.propertyType) items.push({ label: "Type", value: property.propertyType });
  if (property.bedrooms != null)
    items.push({ label: "Bedrooms", value: property.bedrooms });
  if (property.bathrooms != null)
    items.push({ label: "Bathrooms", value: property.bathrooms });
  if (property.squareFeet != null)
    items.push({ label: "Approx. Sq Ft", value: property.squareFeet.toLocaleString("en-US") });
  if (property.condition) items.push({ label: "Condition", value: property.condition });
  if (property.monthlyCharges != null)
    items.push({
      label: "Monthly",
      value: (
        <>
          {formatPriceFull(property.monthlyCharges)}
          {property.chargesNote ? (
            <span className="mt-0.5 block text-xs normal-case tracking-normal text-charcoal-muted">
              {property.chargesNote}
            </span>
          ) : null}
        </>
      ),
    });
  if (property.purchasePrice != null)
    items.push({
      label: "Purchased",
      value: (
        <>
          {formatPriceFull(property.purchasePrice)}
          {property.purchaseDate ? (
            <span className="mt-0.5 block text-xs normal-case tracking-normal text-charcoal-muted">
              {property.purchaseDate}
            </span>
          ) : null}
        </>
      ),
    });

  return (
    <section
      className="w-full bg-ivory py-20 md:py-28"
      data-testid="sa-property"
      aria-label="Your property"
    >
      <div className="mx-auto grid w-full max-w-5xl grid-cols-1 gap-12 px-6 md:px-10 lg:grid-cols-2 lg:items-center lg:gap-16">
        <Reveal className="order-1 lg:order-none">
          {image && (
            <figure className="overflow-hidden">
              <img
                src={image.url}
                alt={image.alt || property.address}
                loading="lazy"
                decoding="async"
                className="aspect-[4/5] w-full object-cover"
              />
            </figure>
          )}
        </Reveal>

        <Reveal delay={0.1}>
          <Overline>Your property</Overline>
          {property.building && (
            <p className="mt-4 text-sm uppercase tracking-[0.2em] text-brass">
              {property.building}
            </p>
          )}
          <h2 className="mt-2 font-serif text-3xl leading-tight text-charcoal md:text-4xl">
            {property.address}
            {property.unit ? (
              <span className="text-charcoal-soft">, {property.unit}</span>
            ) : null}
          </h2>
          {location && (
            <p className="mt-2 text-sm uppercase tracking-[0.18em] text-charcoal-muted">
              {location}
            </p>
          )}

          {items.length > 0 && (
            <dl className="mt-9 grid grid-cols-2 gap-y-7 border-t border-charcoal/10 pt-8 sm:grid-cols-3">
              {items.map((it) => (
                <div key={it.label} className="pr-4">
                  <dd className="font-serif text-xl text-charcoal md:text-2xl">
                    {it.value}
                  </dd>
                  <dt className="mt-1 text-[0.68rem] uppercase tracking-[0.16em] text-charcoal-muted">
                    {it.label}
                  </dt>
                </div>
              ))}
            </dl>
          )}

          {property.notes && (
            <p className="mt-8 max-w-prose border-l-2 border-brass pl-5 font-serif text-lg italic leading-snug text-charcoal md:text-xl">
              {property.notes}
            </p>
          )}
        </Reveal>
      </div>
    </section>
  );
};
